import React, { useState } from "react";
import {
  YEAR,
  STAFFS,
  DEPARTMENTS,
  DEPARTMENT_STAFFS,
  APPRAISERS,
  Status,
  COMPANY_STAFFS,
  COMPANIES,
} from "../Constants";
import { get, post } from "axios";
import { checkExcelAgainstDb } from "./FoobarExcelCheck";

/*
 * Yearly setup page. Run the steps in order after updating the lists in
 * Constants.js and YEAR. Every step logs to the browser console (F12) under
 * its own group, and saves nothing if any name or duplicate check fails.
 *
 * Note: Step 3 clears the company staff lists and Step 5 clears the
 * department staff lists, so re-run Step 4 / Step 6 after them.
 */

const CONCURRENCY = 8;

class StepAborted extends Error {}

const abort = (message, table) => {
  console.error(message);
  if (table) console.table(table);
  throw new StepAborted(message);
};

// Status 0 / no response means the request never completed (connection
// dropped, timeout, proxy); worth retrying, unlike a 404 from the server.
const isNetworkError = (err) =>
  !!err.request && !(err.response && err.response.status);

const withRetry = async (fn, attempts = 3) => {
  for (let i = 1; ; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i >= attempts || !isNetworkError(err)) throw err;
      await new Promise((resolve) => setTimeout(resolve, 1000 * i));
    }
  }
};

const errorText = (err) =>
  isNetworkError(err)
    ? `network error, no response from server (${err.message})`
    : !err.response
    ? err.message
    : `${err.response.status} ${
        err.response.data?.message ||
        (typeof err.response.data === "string"
          ? err.response.data
          : JSON.stringify(err.response.data))
      }`;

// The server matches fullname with a case-insensitive $regex, so escape
// special characters and anchor it to avoid partial matches
// (e.g. "HO AI CHIN" matching "CHO AI CHING").
const toExactPattern = (name) =>
  "^\\s*" +
  name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("\\s+") +
  "\\s*$";

const clean = (s) => (s || "").trim();

// Runs fn over items, a few at a time. Returns [{ item, value } | { item, error }].
const runPool = async (items, fn, label) => {
  const results = new Array(items.length);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { item: items[i], value: await fn(items[i]) };
      } catch (err) {
        results[i] = { item: items[i], error: errorText(err) };
      }
      done++;
      if (done % 50 === 0 || done === items.length) {
        console.log(`  ${label}: ${done}/${items.length}`);
      }
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return results;
};

// Looks up each distinct name once. Returns { users: {name: user}, errors: {name: text} }.
const lookupUsers = async (names) => {
  const distinct = [...new Set(names.filter(Boolean))];
  console.log(`Looking up ${distinct.length} distinct names (year ${YEAR})...`);
  const results = await runPool(
    distinct,
    (name) =>
      withRetry(() =>
        post(`api/users/findByName/year/${YEAR}`, {
          fullname: toExactPattern(name),
        })
      ).then((res) => {
        if (!res.data || !res.data.userid) throw new Error("No record found");
        return res.data;
      }),
    "looked up"
  );
  const users = {};
  const errors = {};
  results.forEach((r) => {
    if (r.error) errors[r.item] = r.error;
    else users[r.item] = r.value;
  });
  return { users, errors };
};

// Aborts if any name in the given fields of rows was not found.
const requireAllFound = (rows, fields, users, errors) => {
  const missing = [];
  rows.forEach((r, i) => {
    fields.forEach((field) => {
      const name = r[field];
      if (name && !users[name]) {
        missing.push({ row: i + 1, field, name, error: errors[name] });
      }
    });
  });
  if (missing.length > 0) {
    abort(
      `Aborted: ${missing.length} name(s) not found in users for ${YEAR}. Nothing was saved. Fix the names in Constants.js or import the users first:`,
      missing
    );
  }
  console.log("All names found.");
};

// Aborts if keyFn gives the same key for more than one row.
const requireUnique = (rows, keyFn, what) => {
  const counts = {};
  rows.forEach((r) => {
    const key = keyFn(r).toUpperCase();
    counts[key] = (counts[key] || 0) + 1;
  });
  const dups = Object.entries(counts)
    .filter(([, n]) => n > 1)
    .map(([key, n]) => ({ [what]: key, times: n }));
  if (dups.length > 0) {
    abort(
      `Aborted: ${dups.length} ${what}(s) listed more than once. Nothing was saved:`,
      dups
    );
  }
};

// Step 1: update position / job grade (and date joined, if given) from STAFFS.
// Only non-empty values are written, so a row with just date_joined keeps the
// existing position/grade. A name listed twice is merged (e.g. one row with
// position/grade, another with date_joined) unless the values conflict.
const STAFF_FIELDS = ["position", "job_grade", "date_joined"];

const step1UpdatePositionGrade = async () => {
  console.log(`${STAFFS.length} rows in STAFFS`);
  const merged = {};
  const conflicts = [];
  STAFFS.forEach((s, i) => {
    const fullname = clean(s.fullname);
    const key = fullname.toUpperCase();
    const row = (merged[key] = merged[key] || { fullname, rows: [] });
    row.rows.push(i + 1);
    STAFF_FIELDS.forEach((f) => {
      const v = clean(s[f]);
      if (!v) return;
      if (row[f] && row[f] !== v) {
        conflicts.push({ staff: fullname, field: f, first: row[f], second: v });
      } else {
        row[f] = v;
      }
    });
  });
  if (conflicts.length > 0) {
    abort(
      `Aborted: ${conflicts.length} staff listed more than once with different values. Nothing was saved:`,
      conflicts
    );
  }
  const rows = Object.values(merged);
  const repeated = rows.filter((r) => r.rows.length > 1);
  if (repeated.length > 0) {
    console.warn(`Merged ${repeated.length} staff listed more than once:`);
    console.table(repeated.map((r) => ({ staff: r.fullname, rows: r.rows.join(", ") })));
  }

  const { users, errors } = await lookupUsers(rows.map((r) => r.fullname));
  requireAllFound(rows, ["fullname"], users, errors);

  const results = await runPool(
    rows,
    (r) => {
      const record = { ...users[r.fullname] };
      STAFF_FIELDS.forEach((f) => {
        if (r[f]) record[f] = r[f];
      });
      return post("api/users", record);
    },
    "updated"
  );
  const failed = results
    .filter((r) => r.error)
    .map((r) => ({ staff: r.item.fullname, error: r.error }));
  if (failed.length > 0) {
    console.error(`${failed.length} update(s) failed:`);
    console.table(failed);
    return `${rows.length - failed.length} updated, ${failed.length} failed`;
  }
  return `${rows.length} staff updated`;
};

// Step 2: replace the appraisee list with APPRAISERS (staff -> appraiser, optional proxy).
// WARNING: the server marks ALL existing appraisee records obsolete (every year).
const step2AssignAppraiser = async () => {
  const rows = APPRAISERS.map((a) => ({
    staff: clean(a.staff),
    appraiser: clean(a.appraiser),
    proxy: clean(a.proxy),
  }));
  console.log(`${rows.length} rows in APPRAISERS`);
  requireUnique(rows, (r) => r.staff, "staff");

  const { users, errors } = await lookupUsers(
    rows.flatMap((r) => [r.staff, r.appraiser, r.proxy])
  );
  requireAllFound(rows, ["staff", "appraiser", "proxy"], users, errors);

  const appraisees = rows.map((r) => {
    const appraiser = {
      appraiser: users[r.appraiser],
      score: 0,
      status: Status.PENDING,
    };
    if (r.proxy) appraiser.proxy = users[r.proxy];
    return {
      ...users[r.staff],
      appraisers: [appraiser],
      status: Status.PENDING,
    };
  });
  console.table(
    rows.map((r) => ({
      staff: r.staff,
      userid: users[r.staff].userid,
      appraiser: r.appraiser,
      proxy: r.proxy,
    }))
  );

  console.log(`Posting ${appraisees.length} appraisees...`);
  await post("api/appraisees", appraisees);
  return `${appraisees.length} appraisees saved`;
};

// Step 3 / Step 5: replace the company or department list for YEAR.
const uploadGroups = async (kind, groups) => {
  requireUnique(groups, (g) => g.name, kind);
  requireUnique(groups, (g) => g[`${kind}id`], `${kind}id`);
  console.table(groups);
  console.log(`Posting ${groups.length} ${kind}s for ${YEAR}...`);
  await post(`api/${kind === "company" ? "companies" : "departments"}/year/${YEAR}`, groups);
  console.warn(
    `Existing ${kind} staff lists for ${YEAR} were cleared. Run "Assign ${
      kind === "company" ? "Company" : "Department"
    } Staff" next.`
  );
  return `${groups.length} ${kind}s saved`;
};

const step3UploadCompanies = () =>
  uploadGroups(
    "company",
    COMPANIES.map((c) => ({
      companyid: clean(c).replace(/\s+/g, "").toLowerCase(),
      name: clean(c),
    }))
  );

const step5UploadDepartments = () =>
  uploadGroups(
    "department",
    DEPARTMENTS.map((d) => ({
      departmentid: clean(d.departmentid),
      name: clean(d.name),
    }))
  );

// Step 4 / Step 6: fill each company / department with its staff from the list.
const assignGroupStaff = async (kind, list) => {
  const path = kind === "company" ? "companies" : "departments";
  const rows = list.map((r) => ({ staff: clean(r.staff), group: clean(r[kind]) }));
  console.log(`${rows.length} rows in ${kind === "company" ? "COMPANY_STAFFS" : "DEPARTMENT_STAFFS"}`);

  const response = await get(`api/${path}/year/${YEAR}`);
  const groups = response.data.map((g) => {
    const { staff_doc, ...rest } = g;
    return rest;
  });
  if (groups.length === 0) {
    abort(`Aborted: no ${kind}s found for ${YEAR}. Upload them first.`);
  }
  const groupNames = new Set(groups.map((g) => g.name.toUpperCase()));

  const unknown = rows
    .map((r, i) => ({ row: i + 1, staff: r.staff, [kind]: r.group }))
    .filter((r) => !groupNames.has(r[kind].toUpperCase()));
  if (unknown.length > 0) {
    abort(
      `Aborted: ${unknown.length} row(s) use a ${kind} name that doesn't exist for ${YEAR}. Nothing was saved:`,
      unknown
    );
  }

  // Exact repeats are harmless: drop them. Same staff in two groups is an error.
  const byStaff = {};
  const unique = [];
  rows.forEach((r) => {
    const key = r.staff.toUpperCase();
    const groupsForStaff = (byStaff[key] = byStaff[key] || new Set());
    if (!groupsForStaff.has(r.group.toUpperCase())) unique.push(r);
    groupsForStaff.add(r.group.toUpperCase());
  });
  if (unique.length < rows.length) {
    console.warn(`Ignoring ${rows.length - unique.length} repeated row(s).`);
  }
  const conflicts = Object.entries(byStaff)
    .filter(([, g]) => g.size > 1)
    .map(([staff, g]) => ({ staff, [path]: [...g].join(" | ") }));
  if (conflicts.length > 0) {
    abort(
      `Aborted: ${conflicts.length} staff assigned to more than one ${kind}. Nothing was saved:`,
      conflicts
    );
  }

  const { users, errors } = await lookupUsers(unique.map((r) => r.staff));
  requireAllFound(unique, ["staff"], users, errors);

  const newGroups = groups.map((g) => ({
    ...g,
    staffs: unique
      .filter((r) => r.group.toUpperCase() === g.name.toUpperCase())
      .map((r) => ({ userid: users[r.staff].userid })),
  }));
  console.table(newGroups.map((g) => ({ [kind]: g.name, staff: g.staffs.length })));
  const empty = newGroups.filter((g) => g.staffs.length === 0);
  if (empty.length > 0) {
    console.warn(`${empty.length} ${kind}(s) have no staff: ${empty.map((g) => g.name).join(", ")}`);
  }

  console.log(`Posting ${newGroups.length} ${kind}s...`);
  await post(`api/${path}/year/${YEAR}`, newGroups);
  return `${unique.length} staff assigned to ${newGroups.length} ${kind}s`;
};

const step4AssignCompanyStaff = () => assignGroupStaff("company", COMPANY_STAFFS);
const step6AssignDepartmentStaff = () => assignGroupStaff("department", DEPARTMENT_STAFFS);

const STEPS = [
  { label: "Step 1: Update Position & Grade", run: step1UpdatePositionGrade },
  { label: "Step 2: Assign Appraiser", run: step2AssignAppraiser },
  { label: "Step 3: Upload Companies", run: step3UploadCompanies },
  { label: "Step 4: Assign Company Staff", run: step4AssignCompanyStaff },
  { label: "Step 5: Upload Department", run: step5UploadDepartments },
  { label: "Step 6: Assign Department Staff", run: step6AssignDepartmentStaff },
];

const Foobar = (props) => {
  const [running, setRunning] = useState(null);
  const [lastResult, setLastResult] = useState("");
  const [report, setReport] = useState(null);

  // step.run returns a summary string, or { summary, report } for the Excel check.
  const runStep = async (step) => {
    setRunning(step.label);
    setReport(null);
    setLastResult(`${step.label}: running... (see console)`);
    console.group(`[${step.label}] year ${YEAR}`);
    try {
      const out = await step.run();
      const summary = typeof out === "string" ? out : out.summary;
      if (out.report) setReport(out.report);
      console.log(`Done: ${summary}.`);
      setLastResult(`${step.label}: done, ${summary}.`);
    } catch (err) {
      if (!(err instanceof StepAborted)) {
        console.error("Failed:", errorText(err), err);
      }
      setLastResult(`${step.label}: failed, nothing saved. See console (F12) for details.`);
    } finally {
      console.groupEnd();
      setRunning(null);
    }
  };

  const handleExcelChosen = (ev) => {
    const file = ev.target.files[0];
    ev.target.value = ""; // allow choosing the same file again
    if (!file) return;
    runStep({
      label: `Check Excel "${file.name}"`,
      run: () => checkExcelAgainstDb(file),
    });
  };

  return (
    <div className="card">
      <div className="card-body">
        <p>
          Setup for year <b>{YEAR}</b>. Results are logged in the browser
          console (F12).
        </p>
        {lastResult && <p>{lastResult}</p>}
        {report && (
          <ul>
            {report.problems.map((p) => (
              <li key={p.category} style={{ color: "#b02a37" }}>
                {p.category}: {p.count}
              </li>
            ))}
            {report.notes.map((n) => (
              <li key={n.category} style={{ color: "#6c757d" }}>
                {n.category}: {n.count}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="card-footer">
        <label>
          Check Excel against database (read-only):{" "}
          <input
            type="file"
            accept=".xlsx"
            disabled={running !== null}
            onChange={handleExcelChosen}
          />
        </label>
      </div>
      <br />
      {STEPS.map((step) => (
        <React.Fragment key={step.label}>
          <div className="card-footer">
            <button disabled={running !== null} onClick={() => runStep(step)}>
              {step.label}
            </button>
          </div>
          <br />
        </React.Fragment>
      ))}
    </div>
  );
};
export default Foobar;
